import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { LuX as X, LuSparkles as Sparkles, LuLock as Lock } from 'react-icons/lu';
import posthog from 'posthog-js';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  reason: 'credits_exhausted' | 'feature_locked';
  feature?: string;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  isOpen,
  onClose,
  reason,
  feature,
}) => {
  const { loginWithRedirect, isAuthenticated } = useAuth0();

  if (!isOpen) return null;

  const handleSignUp = () => {
    posthog.capture('upgrade_prompt_signup_clicked', { reason, feature });
    loginWithRedirect({
      appState: { returnTo: '/app' },
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  };

  const handleLogin = () => {
    posthog.capture('upgrade_prompt_login_clicked', { reason, feature });
    loginWithRedirect({
      appState: { returnTo: '/app' },
    });
  };

  const handleUpgradeToPro = () => {
    posthog.capture('upgrade_prompt_pro_clicked', { reason, feature });
    // TODO: Integrate with Stripe or payment system
    window.location.href = '/#pricing';
  };

  const isCreditsExhausted = reason === 'credits_exhausted';
  const isFeatureLocked = reason === 'feature_locked';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
            {isCreditsExhausted ? (
              <Sparkles className="w-8 h-8 text-white" />
            ) : (
              <Lock className="w-8 h-8 text-white" />
            )}
          </div>
        </div>

        {/* Title & Description */}
        {isCreditsExhausted && !isAuthenticated && (
          <>
            <h2 className="text-2xl font-bold text-center mb-3 text-gray-900">
              You've Used Your Free Tries
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Sign up for a free account to get <strong>10 generations per day</strong>, or upgrade to Pro for unlimited access.
            </p>
          </>
        )}

        {isCreditsExhausted && isAuthenticated && (
          <>
            <h2 className="text-2xl font-bold text-center mb-3 text-gray-900">
              Daily Limit Reached
            </h2>
            <p className="text-gray-600 text-center mb-6">
              You've used all 10 free generations today. Upgrade to Pro for unlimited name generations and advanced features.
            </p>
          </>
        )}

        {isFeatureLocked && !isAuthenticated && (
          <>
            <h2 className="text-2xl font-bold text-center mb-3 text-gray-900">
              Sign Up to Save Your Work
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Create a free account to save your favorite names, access them from anywhere, and never lose your work.
            </p>
          </>
        )}

        {isFeatureLocked && isAuthenticated && (
          <>
            <h2 className="text-2xl font-bold text-center mb-3 text-gray-900">
              Unlock Pro Features
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Upgrade to Pro to access {feature || 'this feature'} and unlock the full power of Namin8.
            </p>
          </>
        )}

        {/* Benefits List */}
        {!isAuthenticated && (
          <div className="bg-indigo-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Free Account Includes:</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>10 name generations per day</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Save favorite names</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Access from any device</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Basic BRAND FIT scoring</span>
              </li>
            </ul>
          </div>
        )}

        {isAuthenticated && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Pro Features:</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">✓</span>
                <span><strong>Unlimited</strong> name generations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">✓</span>
                <span>Full BRAND FIT scoring (8 dimensions)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">✓</span>
                <span>Multi-TLD domain checks</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">✓</span>
                <span>Compare names side-by-side</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">✓</span>
                <span>Export to CSV & share sessions</span>
              </li>
            </ul>
          </div>
        )}

        {/* CTA Buttons */}
        <div className="space-y-3">
          {!isAuthenticated ? (
            <>
              <button
                onClick={handleSignUp}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition shadow-lg"
              >
                Sign Up Free
              </button>
              <button
                onClick={handleLogin}
                className="w-full bg-white text-gray-700 py-3 rounded-lg font-semibold border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition"
              >
                Already have an account? Log In
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleUpgradeToPro}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition shadow-lg"
              >
                Upgrade to Pro - $5.95/month
              </button>
              <button
                onClick={onClose}
                className="w-full text-gray-600 py-2 rounded-lg font-medium hover:text-gray-800 transition"
              >
                Maybe Later
              </button>
            </>
          )}
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          {!isAuthenticated 
            ? 'No credit card required. Cancel anytime.'
            : 'Join thousands of founders finding their perfect brand name'}
        </p>
      </div>
    </div>
  );
};

// Inline prompt for locked features (not a modal)
export const InlineUpgradePrompt: React.FC<{ feature: string }> = ({ feature }) => {
  const { loginWithRedirect, isAuthenticated } = useAuth0();

  const handleClick = () => {
    if (!isAuthenticated) {
      posthog.capture('inline_upgrade_prompt_clicked', { feature, action: 'signup' });
      loginWithRedirect({
        appState: { returnTo: '/app' },
        authorizationParams: {
          screen_hint: 'signup',
        },
      });
    } else {
      posthog.capture('inline_upgrade_prompt_clicked', { feature, action: 'upgrade' });
      window.location.href = '/#pricing';
    }
  };

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-indigo-600" />
        <div>
          <p className="font-medium text-gray-900 text-sm">
            {isAuthenticated ? 'Upgrade to Pro' : 'Sign Up Free'}
          </p>
          <p className="text-xs text-gray-600">
            {isAuthenticated 
              ? `Unlock ${feature} and unlimited generations`
              : `Sign up to ${feature} and get 10 generations/day`
            }
          </p>
        </div>
      </div>
      <button
        onClick={handleClick}
        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition whitespace-nowrap"
      >
        {isAuthenticated ? 'Upgrade' : 'Sign Up'}
      </button>
    </div>
  );
};

